/**
 * AllTransactionsList
 * Unified transaction table for all 6 inventory types merged into one view.
 * Each row carries _inventoryType and _config tags for per-type rendering.
 *
 * Columns: Date | Inv Type | Trans Type | Item | Location | Quantity | Notes
 */

import React from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TablePagination, useTheme, alpha
} from '@mui/material';
import {
  TRANSACTION_TYPE_MAP,
  formatTransactionDate
} from '../../transactions-components/transactionConstants';

// ── Type chip styles ──────────────────────────────────────────────────────────

const TYPE_STYLES = {
  products:  { label: 'Product',  color: '#1565c0', bg: '#e3f2fd' },
  chemicals: { label: 'Chemical', color: '#7b1fa2', bg: '#f3e5f5' },
  caps:      { label: 'Cap',      color: '#00796b', bg: '#e0f2f1' },
  bottles:   { label: 'Bottle',   color: '#2e7d32', bg: '#e8f5e9' },
  labels:    { label: 'Label',    color: '#e65100', bg: '#fff3e0' },
  supplies:  { label: 'Supply',   color: '#5d4037', bg: '#efebe9' },
};

// ── Header / cell styles ──────────────────────────────────────────────────────

const hSx = (theme) => ({
  fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  color: 'primary.main', py: 2, px: 2, whiteSpace: 'nowrap'
});

const cSx = (theme) => ({
  py: 2.5, px: 2,
  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`
});

// ── Per-type helpers ──────────────────────────────────────────────────────────

function getItemName(tx) {
  if (tx._inventoryType === 'products') return tx.products?.name || 'Unknown';
  return tx[tx._config?.tableName]?.name || 'Unknown';
}

function getItemSku(tx) {
  if (tx._inventoryType === 'products') return tx.products?.sku || '';
  return tx[tx._config?.tableName]?.sku || '';
}

function getVariant(tx) {
  if (tx._inventoryType === 'products') return tx.variants?.name || null;
  return null;
}

function LocationDisplay({ tx }) {
  const isTransfer = tx.transaction_type === 'transfer';
  if (isTransfer) {
    const from = tx.from_location?.name || 'Unknown';
    const to   = tx.to_location?.name   || 'Unknown';
    return (
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
        {from} → {to}
      </Typography>
    );
  }
  const loc = tx.location?.name || tx.from_location?.name || tx.to_location?.name || '-';
  return (
    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.secondary' }}>
      {loc}
    </Typography>
  );
}

function QuantityDisplay({ tx }) {
  const qty = tx.quantity;
  if (qty === null || qty === undefined) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  const num   = parseFloat(qty);
  const isNeg = num < 0;
  const color = isNeg ? 'error.main' : 'success.main';
  const sign  = isNeg ? '' : '+';
  const abs   = Math.abs(num);

  // Products: show cases
  if (tx._inventoryType === 'products') {
    const upc = tx.units_per_case || 1;
    if (upc > 1) {
      const cases = Math.floor(abs / upc);
      const units = abs % upc;
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color }}>
            {sign}{cases.toLocaleString()} {cases === 1 ? 'case' : 'cases'}
            {units > 0 ? ` + ${units}u` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {abs.toLocaleString()} units total
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color }}>
        {sign}{abs.toLocaleString()} units
      </Typography>
    );
  }

  // Chemicals: show unit type + total
  if (tx._config?.hasUnitOfMeasure) {
    const item        = tx[tx._config.tableName];
    const unitType    = item?.primary_unit_type    || 'units';
    const unitMeasure = item?.primary_unit_measure || '';
    const totalQty    = tx.total_quantity != null ? parseFloat(tx.total_quantity) : null;
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color }}>
          {sign}{abs.toLocaleString()} {unitType}
        </Typography>
        {totalQty != null && unitMeasure && (
          <Typography variant="caption" color="text.secondary">
            ({isNeg ? '' : '+'}{Math.abs(totalQty).toLocaleString()} {unitMeasure})
          </Typography>
        )}
      </Box>
    );
  }

  // Generics: plain number
  return (
    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color }}>
      {sign}{abs.toLocaleString()}
    </Typography>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AllTransactionsList({
  transactions, totalCount, page, rowsPerPage,
  onPageChange, onRowsPerPageChange, timeFormat, onViewTransaction
}) {
  const theme = useTheme();

  return (
    <Paper sx={{
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <TableContainer sx={{ overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hSx(theme), width: '13%' }}>Date</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '10%' }}>Inv Type</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '11%' }}>Trans Type</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '20%' }}>Item</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '20%' }}>Location</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '14%' }}>Quantity</TableCell>
              <TableCell sx={{ ...hSx(theme), width: '12%' }}>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, idx) => {
              const ts       = TYPE_STYLES[tx._inventoryType] || TYPE_STYLES.supplies;
              const typeInfo = TRANSACTION_TYPE_MAP[tx.transaction_type];
              const name     = getItemName(tx);
              const sku      = getItemSku(tx);
              const variant  = getVariant(tx);

              return (
                <TableRow
                  key={`${tx._inventoryType}-${tx.id}`}
                  hover
                  onClick={() => onViewTransaction(tx)}
                  sx={{
                    cursor: 'pointer', transition: 'all 0.2s ease',
                    bgcolor: idx % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.8),
                    '&:hover': {
                      bgcolor: 'grey.50', transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    }
                  }}
                >
                  {/* Date */}
                  <TableCell sx={cSx(theme)}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {formatTransactionDate(tx.transaction_date || tx.created_at, timeFormat)}
                    </Typography>
                  </TableCell>

                  {/* Inv Type */}
                  <TableCell sx={cSx(theme)}>
                    <Chip
                      label={ts.label}
                      size="small"
                      sx={{
                        bgcolor: ts.bg, color: ts.color, fontWeight: 500,
                        border: `1px solid ${ts.color}55`, fontSize: '0.72rem'
                      }}
                    />
                  </TableCell>

                  {/* Trans Type */}
                  <TableCell sx={cSx(theme)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      {typeInfo ? (
                        <Chip
                          icon={typeInfo.icon}
                          label={typeInfo.label}
                          size="small"
                          color={typeInfo.color}
                          sx={{
                            fontWeight: 500, fontSize: '0.8rem', height: 28,
                            border: '2px solid',
                            borderColor: theme.palette[typeInfo.color]?.main || theme.palette.primary.main
                          }}
                        />
                      ) : (
                        <Chip label={tx.transaction_type || 'Unknown'} size="small" />
                      )}
                      {tx.po_number && (
                        <Chip label={`PO #${tx.po_number}`} size="small" variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', borderColor: 'primary.main', color: 'primary.main' }} />
                      )}
                    </Box>
                  </TableCell>

                  {/* Item */}
                  <TableCell sx={cSx(theme)}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem', color: 'text.primary' }}>
                      {name}
                    </Typography>
                    {sku && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        SKU: {sku}
                      </Typography>
                    )}
                    {variant && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {variant}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Location */}
                  <TableCell sx={cSx(theme)}>
                    <LocationDisplay tx={tx} />
                  </TableCell>

                  {/* Quantity */}
                  <TableCell sx={cSx(theme)}>
                    <QuantityDisplay tx={tx} />
                  </TableCell>

                  {/* Notes */}
                  <TableCell sx={cSx(theme)}>
                    {tx.notes?.startsWith('PI Delivery Deduction') ? (() => {
                      const piNumMatch = tx.notes.match(/PI #([^\s|—]+)/);
                      const custMatch  = tx.notes.match(/Customer:\s*([^—|]+)/);
                      const compMatch  = tx.notes.match(/Company:\s*([^—|]+)/);
                      const piNumber   = piNumMatch?.[1]?.trim();
                      const customer   = custMatch?.[1]?.trim();
                      const company    = compMatch?.[1]?.trim();
                      return (
                        <Box>
                          <Chip
                            label={piNumber ? `PI #${piNumber}` : 'PI Delivery'}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 500, fontSize: '0.72rem', height: 20, mb: 0.5 }}
                          />
                          {customer && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                              {customer}
                            </Typography>
                          )}
                          {company && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                              {company}
                            </Typography>
                          )}
                        </Box>
                      );
                    })() : (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.85rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}
                        title={tx.notes || ''}
                      >
                        {tx.notes || '-'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No transactions found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6, px: 3 }}>
        <Paper sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden'
        }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[50, 100, 150]}
          />
        </Paper>
      </Box>
    </Paper>
  );
}
